import React from "react";

const sectors = [
  {
    name: "Manufacturing",
    examples: [
      "Machine setup always done same way despite changing raw material (High Bias)",
      "Different operator uses different settings every shift (High Variance)",
      "Balanced approach: SOP + data monitoring"
    ]
  },
  {
    name: "Retail",
    examples: [
      "Owner sets prices purely on intuition (High Bias)",
      "Prices change randomly every week (High Variance)",
      "Balanced approach: track sales data + controlled experiments"
    ]
  },
  {
    name: "Agriculture",
    examples: [
      "Farmers always use same fertilizer method (High Bias)",
      "Trying many techniques without tracking results (High Variance)",
      "Balanced approach: experiment but record outcomes"
    ]
  },
  {
    name: "Healthcare Clinics",
    examples: [
      "Doctor always prescribes same treatment protocol (High Bias)",
      "Different treatment for same case every visit (High Variance)",
      "Balanced approach: standard protocols + patient data"
    ]
  },
  {
    name: "Education / Training",
    examples: [
      "Teacher always follows same method regardless of student needs (High Bias)",
      "Changing teaching style every week without measuring impact (High Variance)",
      "Balanced approach: structured curriculum + student feedback"
    ]
  },
  {
    name: "Hotels / Restaurants (HORECA)",
    examples: [
      "Chef refuses to change recipe even when customers complain (High Bias)",
      "Dish quality different every day (High Variance)",
      "Balanced approach: recipe standards + feedback tracking"
    ]
  }
];

const BVAbout = () => {
  return (
    <div className="bg-black text-white [&_p]:text-white [&_li]:text-gray-100 [&_h2]:text-yellow-300 min-h-screen px-4 py-6">

      {/* Header */}
      <h1 className="text-2xl font-bold mb-4">
        ⚖️ Bias–Variance Knowledge
      </h1>

      <p className="text-gray-300 text-sm mb-6">
        Bias and Variance are two simple ideas that explain why many business decisions fail. Understanding them helps MSMEs improve consistency,
        quality, and growth.
      </p>

      {/* What is Bias */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-3">🧠 What is Bias?</h2>

        <p className="text-gray-300 text-sm">
          Bias means relying too much on fixed assumptions, habits, or old experience without checking real data.
        </p>

        <ul className="list-disc pl-5 text-gray-400 text-sm mt-3 space-y-1">
          <li>“This is how we have always done it.”</li>
          <li>Ignoring new information.</li>
          <li>Decisions based only on intuition.</li>
        </ul>
      </section>

      {/* What is Variance */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-3">📊 What is Variance?</h2>

        <p className="text-gray-300 text-sm">
          Variance means too much randomness in processes or decisions. The same task gives different results each time.
        </p>

        <ul className="list-disc pl-5 text-gray-400 text-sm mt-3 space-y-1">
          <li>No standard process.</li>
          <li>Different people doing things differently.</li>
          <li>Inconsistent quality or output.</li>
        </ul>
      </section>

      {/* Balanced */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">⚖️ Balanced Approach</h2>

        <p className="text-gray-300 text-sm">
          The goal is not to eliminate bias or variance completely but to keep them balanced.
        </p>

        <ul className="list-disc pl-5 text-gray-400 text-sm mt-3 space-y-1">
          <li>Use experience but validate with data.</li>
          <li>Allow improvement but maintain standards.</li>
          <li>Measure results before changing processes.</li>
        </ul>
      </section>

      {/* Sector Knowledge */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-4">
          🏭 Sector-wise Practical Examples
        </h2>

        <div className="space-y-5">
          {sectors.map((sector, index) => (
            <div
              key={index}
              className="border border-gray-700 rounded-lg p-4"
            >
              <h3 className="font-semibold mb-2 text-lg text-cyan-300">
                {sector.name}
              </h3>

              <ul className="list-disc pl-5 text-gray-200 text-sm space-y-1">
                {sector.examples.map((ex, i) => (
                  <li key={i}>{ex}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Why MSME Should Care */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">
          🚀 Why MSMEs Should Care
        </h2>

        <ul className="list-disc pl-5 text-gray-400 text-sm space-y-1">
          <li>Reduces business mistakes.</li>
          <li>Improves quality and consistency.</li>
          <li>Helps scale operations.</li>
          <li>Supports digital transformation.</li>
          <li>Makes AI adoption easier.</li>
        </ul>
      </section>

      {/* Practical Actions */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">
          🛠 How MSMEs Can Manage Bias–Variance
        </h2>

        <ul className="list-disc pl-5 text-gray-400 text-sm space-y-1">
          <li>Track basic business data (sales, defects, complaints).</li>
          <li>Create simple SOPs for repeated tasks.</li>
          <li>Encourage small experiments but record results.</li>
          <li>Use simple dashboards or spreadsheets.</li>
          <li>Review decisions using data every month.</li>
        </ul>
      </section>

      {/* Final Summary */}
      <section className="border-t border-gray-700 pt-6">
        <h2 className="text-xl font-semibold mb-3">
          📌 Final Summary
        </h2>

        <p className="text-gray-300 text-sm">
          Many MSME problems — inconsistent quality, unpredictable sales, employee confusion, and failed improvements — happen because of
          uncontrolled bias and variance.
        </p>

        <p className="text-gray-300 text-sm mt-3">
          By combining experience with data, and by standardizing processes while allowing measured improvement, MSMEs can create stable and
          scalable businesses.
        </p>

        <p className="text-gray-300 text-sm mt-3">
          This is why the <span className="font-semibold">Think5Whys Bias–Variance framework</span> becomes a powerful foundation for digital transformation,
          operational excellence, and AI readiness.
        </p>
      </section>

    </div>
  );
};

export default BVAbout; 